import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchCockpitUsers, type CockpitUserRow } from "../../lib/admin";

const PAGE = 30;

function fmtDate(s: string | null): string {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function statutBadge(s: string) {
  const base = "rounded-full px-2 py-0.5 text-xs font-medium";
  if (s === "actif") return `${base} bg-emerald-500/20 text-emerald-300`;
  if (s === "dormant") return `${base} bg-amber-500/20 text-amber-200`;
  return `${base} bg-slate-600/50 text-slate-300`;
}

export default function CockpitUsers() {
  const [rows, setRows] = useState<CockpitUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pays, setPays] = useState("");
  const [statut, setStatut] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchCockpitUsers({
      pays: pays || undefined,
      statut: statut || undefined,
      search: search || undefined,
      limit: PAGE,
      offset,
    })
      .then((r) => {
        if (!alive) return;
        setRows(r.rows ?? []);
        setTotal(r.total ?? 0);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [pays, statut, search, offset]);

  const canPrev = offset > 0;
  const canNext = offset + PAGE < total;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">Utilisateurs</h2>
        <p className="mt-1 text-sm text-slate-400">Source : vue logique admin_vue_users (via RPC sécurisé).</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-700/80 bg-slate-800/40 p-4">
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Recherche
          <input
            value={search}
            onChange={(e) => {
              setOffset(0);
              setSearch(e.target.value);
            }}
            placeholder="Email ou nom…"
            className="w-56 rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Pays
          <select
            value={pays}
            onChange={(e) => {
              setOffset(0);
              setPays(e.target.value);
            }}
            className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-white/15"
          >
            <option value="">Tous</option>
            <option value="CI">CI</option>
            <option value="SN">SN</option>
            <option value="CM">CM</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Statut
          <select
            value={statut}
            onChange={(e) => {
              setOffset(0);
              setStatut(e.target.value);
            }}
            className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-white/15"
          >
            <option value="">Tous</option>
            <option value="actif">Actif</option>
            <option value="dormant">Dormant</option>
            <option value="inactif">Inactif</option>
          </select>
        </label>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-700/80 bg-slate-800/30">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-900/50 text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Nom</th>
                  <th className="px-4 py-3 font-medium">Pays</th>
                  <th className="px-4 py-3 font-medium text-right">Dossiers</th>
                  <th className="px-4 py-3 font-medium">Dernière connexion</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/80">
                {rows.map((u) => (
                  <tr key={u.user_id} className="hover:bg-slate-800/50">
                    <td className="px-4 py-3">
                      <Link
                        to={`/cockpit/users/${u.user_id}`}
                        className="font-medium text-orange-400 hover:text-orange-300"
                      >
                        {u.email}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{u.nom_complet || "—"}</td>
                    <td className="px-4 py-3 text-slate-400">{u.pays || "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-300">{u.nb_dossiers}</td>
                    <td className="px-4 py-3 text-slate-400">{fmtDate(u.last_sign_in_at)}</td>
                    <td className="px-4 py-3">
                      <span className={statutBadge(u.statut)}>{u.statut}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-slate-400">
        <span>
          {total === 0 ? "Aucun résultat" : `${offset + 1} – ${Math.min(offset + PAGE, total)} sur ${total}`}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE))}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-3 py-1.5 text-slate-200 disabled:opacity-40"
          >
            <ChevronLeft size={16} /> Précédent
          </button>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setOffset((o) => o + PAGE)}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-3 py-1.5 text-slate-200 disabled:opacity-40"
          >
            Suivant <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
